import React from "react";

import { parse } from "cookie";
import { createRoot, hydrateRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";

import { initUFO } from "@/analytics/react-ufo/initUFO";
import { getAnalyticsWebClient } from "@/analytics/wac-analytics/get-analytics-web-client";
import {
    IntercomProperties,
    IntercomSettingsProperties
} from "@/libraries/intercom/src";
import { isPerformanceOnetrustEnabled } from "@/utils/onetrust-provider";

import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import {
    TemplateCollection,
    type Template
} from "./content/templates/template.types";
import { type AppPricingData } from "./contentful";
import { initializeFeatureGatesClient } from "./feature-gates/initialization";
import { Layout } from "./layout";
import {
    AvailableProductsResponse,
    CcpTransactionAccountData,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "./libraries/bxp-web-stargate-client/src";
import { GrowthUnifiedProfile } from "./libraries/growth-unified-profile-client";
import { PageWrapper } from "./page-wrapper";
import { AuxiaResponse } from "./pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment.tsx";
import {
    overrideAjsAnonIdFromCookie,
    overrideCustomAttributes,
    isEdgeCachingPath
} from "./utils/fg-bootstrap-values-override-utils";
import { GridOverlayFunction } from "./utils/grid-overlay";
import { SentryIntlProvider } from "./utils/sentry-intl";
import { UserLocale, LocationData } from "./utils/useGeoCurrency";
import { processExperimentAndFeatureGateOverrides } from "./utils/wac-browser-utility";
import { parseRoute } from "./wac-routes";

window.BUILD_KEY = process.env["VERSION"];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

declare global {
    // @ts-ignore
    type FeatureFlagType = "boolean" | "string" | "number";

    interface Window {
        IS_STATIC?: boolean;
        BUILD_KEY?: string;
        entryName?: string;
        SSR_languagePack: Record<string, string>;
        prices?: AppPricingData;
        availableProducts?: AvailableProductsResponse;
        userProfile?: UserProfile;
        userProfileStatus?: ServerSideRequestStatus;
        userContext?: UserProfileResponse;
        userContextStatus?: ServerSideRequestStatus;
        loggedOutContext?: GrowthUnifiedProfile;
        loggedOutContextStatus?: ServerSideRequestStatus;
        cmsPageVariant?: string;
        // @ts-ignore
        customersLanding?: CustomersLanding;
        templates?: Template[];
        mlTemplateIds?: string[];
        partnerTemplates?: Template[];
        collections?: TemplateCollection[];
        categories?: TemplateCategory[];
        teamType?: string;
        keyword?: string;
        featureFlags: Record<string, FeatureFlagType>;
        countryCode?: UserLocale;
        regionIsoCode?: string;
        AtlOneTrustWrapper?: { updatePolicyLinks: () => void };
        Intercom?: IntercomProperties;
        intercomSettings?: IntercomSettingsProperties;
        toggleGrid?: GridOverlayFunction;
        isUniformPlayground?: boolean;
        s2sExperimentCallMade?: boolean;
        referrer?: string;
        track?: string;
        useEsmodules?: boolean;
        anchor?: string;
        subAnchor?: string;
        auxiaResponse?: AuxiaResponse | null;
        ssrAnonId?: string;
        transactionAccounts?: Array<CcpTransactionAccountData>;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const init = (Component: React.ComponentType<any>) => {
    const isStatic = typeof window !== "undefined" && window.IS_STATIC;
    const analyticsWebClient = getAnalyticsWebClient();
    const { locale, path } = parseRoute(window.location);
    // Get location data for experiments and feature gates from server-provided data
    let locationData: LocationData | undefined;

    // Use server-provided data (no client-side API calls)

    if (typeof window !== "undefined") {
        locationData = {
            countryIsoCode: window.countryCode || "",
            regionIsoCode: window.regionIsoCode || ""
        };
    }

    const isPerfOnetrustEnabled = isPerformanceOnetrustEnabled(
        document.cookie,
        locationData
    );
    isPerfOnetrustEnabled && initUFO(analyticsWebClient, path);
    const parsedClientCookies = document.cookie ? parse(document.cookie) : {};
    const userAgent =
        typeof window !== "undefined" ? window.navigator.userAgent : "";

    const { finalExperimentOverrides, finalFeatureGateOverrides } =
        processExperimentAndFeatureGateOverrides(
            parsedClientCookies,
            userAgent,
            isPerfOnetrustEnabled
        );

    // Below is for debugging edge - will be removed
    window.ssrAnonId =
        window.featureGateBootstrapValues.identifiers.analyticsAnonymousId;

    // We can't feature gate below code as this is done before initialization so just overriding for the path we are testing
    if (isEdgeCachingPath(window.location.pathname)) {
        // Override for Edge Caching
        overrideAjsAnonIdFromCookie();
        overrideCustomAttributes();
    }

    initializeFeatureGatesClient(window.featureGateBootstrapValues, {
        overrideValues: {
            experimentOverrides: finalExperimentOverrides,
            featureGateOverrides: finalFeatureGateOverrides
        },
        // if performance cookies are disabled, we will skip exposures
        localMode: !isPerfOnetrustEnabled
    }).then(() => {
        if (isStatic) {
            // @ts-ignore
            const root = createRoot(document.getElementById("wac"));
            root.render(
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <PageWrapper
                        analyticsWebClient={analyticsWebClient}
                        pureClientMode={true}
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        userProfile={window.userProfile}
                        userProfileStatus={window.userProfileStatus}
                        userContext={window.userContext}
                        userContextStatus={window.userContextStatus}
                        loggedOutContext={window.loggedOutContext}
                        loggedOutContextStatus={window.loggedOutContextStatus}
                        countryCode={window.countryCode}
                        templates={window.templates}
                        partnerTemplates={window.partnerTemplates}
                        collections={window.collections}
                        categories={window.categories}
                        composition={window._uniformPreloadedComposition}
                        // @ts-ignore
                        compositionMetrics={window.compositionMetrics}
                        isUniformPlayground={window.isUniformPlayground}
                        teamType={window.teamType}
                        keyword={window.keyword}
                        referrer={window.referrer}
                        mlTemplateIds={window.mlTemplateIds}
                        track={window.track}
                        anchor={window.anchor}
                        subAnchor={window.subAnchor}
                        auxiaResponse={window.auxiaResponse}
                        transactionAccounts={window.transactionAccounts}
                    >
                        {Component}
                    </PageWrapper>
                </SentryIntlProvider>
            );
        } else {
            hydrateRoot(
                // @ts-ignore
                document.getElementById("wac-root"),
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                    cookies={document.cookie}
                >
                    <Layout
                        analyticsWebClient={analyticsWebClient}
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        // @ts-ignore
                        tutorial={window.tutorial}
                        templates={window.templates}
                        partnerTemplates={window.partnerTemplates}
                        collections={window.collections}
                        categories={window.categories}
                        userProfile={window.userProfile}
                        userProfileStatus={window.userProfileStatus}
                        userContext={window.userContext}
                        userContextStatus={window.userContextStatus}
                        loggedOutContext={window.loggedOutContext}
                        loggedOutContextStatus={window.loggedOutContextStatus}
                        countryCode={window.countryCode}
                        composition={window._uniformPreloadedComposition}
                        // @ts-ignore
                        compositionMetrics={window.compositionMetrics}
                        isUniformPlayground={window.isUniformPlayground}
                        teamType={window.teamType}
                        keyword={window.keyword}
                        mlTemplateIds={window.mlTemplateIds}
                        referrer={window.referrer}
                        anchor={window.anchor}
                        subAnchor={window.subAnchor}
                        auxiaResponse={window.auxiaResponse}
                        transactionAccounts={window.transactionAccounts}
                    >
                        {Component}
                    </Layout>
                </SentryIntlProvider>,
                {
                    onRecoverableError: (error, errorInfo) => {
                        // add an attribute if in CI
                        if (process.env["CI"] === "true") {
                            document
                                .getElementById("wac-root")
                                ?.setAttribute("hydration-error", "true");
                        }
                        if (process.env["MODE"] === "dev") {
                            console.log(
                                `Recoverable Error (including Hydration): ${error}`,
                                errorInfo
                            );
                        }
                    }
                }
            );
        }
    });
};
